import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { AssistantService } from '../services/AssistantService';
import { useLoadingContext } from '../contexts/LoadingContext';

type AssistantLoadersParams = {
  setError: (key: string, value: string | null) => void;
  setAssistants: (items: any[]) => void;
  selectedAssistantInternalNameRef: MutableRefObject<string>;
  setSelectedAssistantInternalName: (value: string) => void;
  setDocumentAnswerPromptConfigs: (items: any[]) => void;
  setSelectedDocumentAnswerPromptConfigKey: (value: string) => void;
  selectedDocumentAnswerPromptConfigKeyRef: MutableRefObject<string>;
  setAssistantPromptsError: (value: string | null) => void;
  assistantsLoadedRef: MutableRefObject<boolean>;
  assistantsLoadPromiseRef: MutableRefObject<Promise<void> | null>;
  assistantsRetryRef: MutableRefObject<number>;
  MAX_AUTO_RETRIES: number;
};

export const useAssistantLoaders = (params: AssistantLoadersParams) => {
  const { beginLoadingKey } = useLoadingContext();
  const paramsRef = useRef(params);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  const loadAssistants = useCallback(async (accessToken: string, options?: { force?: boolean }) => {
    if (!accessToken) return;
    const force = options?.force ?? false;
    if (!force && paramsRef.current.assistantsLoadedRef.current) return;
    if (!force && paramsRef.current.assistantsLoadPromiseRef.current) return paramsRef.current.assistantsLoadPromiseRef.current;
    paramsRef.current.setError('assistants', null);
    const stopLoading = beginLoadingKey('assistantsLoad');
    const promise = (async () => {
      try {
        const list = await AssistantService.listAssistants(accessToken);
        const nextAssistants = list ?? [];
        paramsRef.current.setAssistants(nextAssistants);

        const defaultAssistant = nextAssistants.find((a: any) => a.isDefault) ?? nextAssistants[0];
        const current = paramsRef.current.selectedAssistantInternalNameRef.current;
        const next = current && nextAssistants.some((a: any) => a.internalName === current)
          ? current
          : (defaultAssistant?.internalName ?? '');

        paramsRef.current.selectedAssistantInternalNameRef.current = next;
        paramsRef.current.setSelectedAssistantInternalName(next);
        if (next !== current) {
          paramsRef.current.setDocumentAnswerPromptConfigs([]);
          paramsRef.current.setSelectedDocumentAnswerPromptConfigKey('');
          paramsRef.current.selectedDocumentAnswerPromptConfigKeyRef.current = '';
          paramsRef.current.setAssistantPromptsError(null);
        }

        paramsRef.current.assistantsLoadedRef.current = true;
        paramsRef.current.assistantsRetryRef.current = 0;
      } catch (err: any) {
        console.error('ListAssistants fehlgeschlagen', err);
        if (paramsRef.current.assistantsRetryRef.current < paramsRef.current.MAX_AUTO_RETRIES) {
          paramsRef.current.assistantsRetryRef.current += 1;
          setTimeout(() => { loadAssistants(accessToken, { force: true }); }, 1000);
        } else {
          paramsRef.current.setError('assistants', err?.message ?? 'Unbekannter Fehler');
        }
      } finally {
        paramsRef.current.assistantsLoadPromiseRef.current = null;
        stopLoading();
      }
    })();

    paramsRef.current.assistantsLoadPromiseRef.current = promise;
    return promise;
  }, [beginLoadingKey]);
  const loadAssistantPrompts = useCallback(async (accessToken: string, assistantInternalName: string) => {
    if (!accessToken) return;
    if (!assistantInternalName) {
      paramsRef.current.setDocumentAnswerPromptConfigs([]);
      paramsRef.current.setSelectedDocumentAnswerPromptConfigKey('');
      paramsRef.current.selectedDocumentAnswerPromptConfigKeyRef.current = '';
      paramsRef.current.setAssistantPromptsError(null);
      return;
    }

    paramsRef.current.setAssistantPromptsError(null);
    const stopLoading = beginLoadingKey('promptsLoad');
    try {
      const prompts = await AssistantService.listAssistantPrompts(accessToken, assistantInternalName);
      const nextPrompts = (prompts ?? []).map((p: any) => ({
        key: p.key,
        displayName: p.displayName,
        isDefault: p.isDefault,
      }));
      paramsRef.current.setDocumentAnswerPromptConfigs(nextPrompts);

      const currentKey = paramsRef.current.selectedDocumentAnswerPromptConfigKeyRef.current;
      const defaultPrompt = nextPrompts.find((c: any) => c.isDefault) ?? nextPrompts[0];
      const nextKey = currentKey && nextPrompts.some((c: any) => c.key === currentKey)
        ? currentKey
        : (defaultPrompt?.key ?? '');

      paramsRef.current.selectedDocumentAnswerPromptConfigKeyRef.current = nextKey;
      paramsRef.current.setSelectedDocumentAnswerPromptConfigKey(nextKey);
    } catch (err: any) {
      console.error('ListAssistantPrompts fehlgeschlagen', err);
      paramsRef.current.setDocumentAnswerPromptConfigs([]);
      paramsRef.current.setSelectedDocumentAnswerPromptConfigKey('');
      paramsRef.current.selectedDocumentAnswerPromptConfigKeyRef.current = '';
      paramsRef.current.setAssistantPromptsError(err?.message ?? 'Unbekannter Fehler');
    } finally {
      stopLoading();
    }
  }, [beginLoadingKey]);
  return { loadAssistants, loadAssistantPrompts };
};
